import * as React from "react";
import {Grid2 as Grid} from "@mui/material";
import {CompanySignUpDTO} from "@/api/data-contracts";
import {ChangeHandler} from "@/lib/modelstate";
import {ErrorsShape} from "@/lib/utils";
import {CardEx} from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import {CompanyI18N} from "@/translations";
import {GroupBox} from "@/lib/components/GroupBox";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";

export const CompanyDetailsForm = ({
    model,
    errors,
    onChange
}: {
    model?: CompanySignUpDTO;
    onChange: ChangeHandler;
    errors: ErrorsShape<CompanySignUpDTO>;
}) => {
    const { t } = useTranslation();
    return (
        <Grid container spacing={2} width="100%">
            <Grid size={{ xs: 12 }}>
                <CardEx title={t(CompanyI18N.form.page.moreDataCompanyTitle)}>
                    <Grid container spacing={3} direction="column" width="100%">
                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <StringInputField
                                name="officeHours"
                                value={model?.officeHours}
                                label={t(CompanyI18N.form.field.officeHours)}
                                error={errors?.officeHours}
                                maxChars={Constants.OFFICE_HOURS_LENGTH_MAX}
                                onChange={onChange}
                                multiline={true}
                            />
                        </Grid>
                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <GroupBox title={t(CompanyI18N.form.field.machineryPark)}>
                                <Grid size={{ xs: 12 }}>
                                    <CheckboxInputField
                                        name="miniliner"
                                        label={t(CompanyI18N.form.field.miniliner)}
                                        onChange={onChange}
                                        value={model?.miniliner}
                                        error={errors?.miniliner}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="midliner"
                                        label={t(CompanyI18N.form.field.midliner)}
                                        onChange={onChange}
                                        value={model?.midliner}
                                        error={errors?.midliner}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="maxiliner"
                                        label={t(CompanyI18N.form.field.maxiliner)}
                                        onChange={onChange}
                                        value={model?.maxiliner}
                                        error={errors?.maxiliner}
                                        dense
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <CheckboxInputField
                                        name="dieCutting"
                                        label={t(CompanyI18N.form.field.dieCutting)}
                                        onChange={onChange}
                                        value={model?.dieCutting}
                                        error={errors?.dieCutting}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="folderGluerMachine"
                                        label={t(CompanyI18N.form.field.folderGluerMachine)}
                                        onChange={onChange}
                                        value={model?.folderGluerMachine}
                                        error={errors?.folderGluerMachine}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="packaging"
                                        label={t(CompanyI18N.form.field.packaging)}
                                        onChange={onChange}
                                        value={model?.packaging}
                                        error={errors?.packaging}
                                        dense
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <CheckboxInputField
                                        name="hazardousGoodsQualification"
                                        label={t(CompanyI18N.form.field.hazardousGoodsQualification)}
                                        onChange={onChange}
                                        value={model?.hazardousGoodsQualification}
                                        error={errors?.hazardousGoodsQualification}
                                        dense
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <CheckboxInputField
                                        name="flexoPrinting"
                                        label={t(CompanyI18N.form.field.flexoPrinting)}
                                        onChange={onChange}
                                        value={model?.flexoPrinting}
                                        error={errors?.flexoPrinting}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="hdFlexoPrinting"
                                        label={t(CompanyI18N.form.field.hdFlexoPrinting)}
                                        onChange={onChange}
                                        value={model?.hdFlexoPrinting}
                                        error={errors?.hdFlexoPrinting}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="offsetPrinting"
                                        label={t(CompanyI18N.form.field.offsetPrinting)}
                                        onChange={onChange}
                                        value={model?.offsetPrinting}
                                        error={errors?.offsetPrinting}
                                        dense
                                    />
                                    <CheckboxInputField
                                        name="digitalPrinting"
                                        label={t(CompanyI18N.form.field.digitalPrinting)}
                                        onChange={onChange}
                                        value={model?.digitalPrinting}
                                        error={errors?.digitalPrinting}
                                        dense
                                    />
                                </Grid>
                            </GroupBox>
                        </Grid>
                    </Grid>
                </CardEx>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <CardEx title={t(CompanyI18N.form.page.erpDataTitle)}>
                    <Grid container spacing={3} direction="column" width="100%">
                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <StringInputField
                                name="erpManufacturer"
                                value={model?.erpManufacturer}
                                label={t(CompanyI18N.form.field.erpManufacturer)}
                                error={errors?.erpManufacturer}
                                maxChars={Constants.ERP_MANUFACTURER_LENGTH_MAX}
                                onChange={onChange}
                                multiline={true}
                            />
                        </Grid>
                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <StringInputField
                                name="erpSoftwareName"
                                value={model?.erpSoftwareName}
                                label={t(CompanyI18N.form.field.erpSoftwareName)}
                                error={errors?.erpSoftwareName}
                                maxChars={Constants.ERP_SOFTWARE_NAME_LENGTH_MAX}
                                onChange={onChange}
                                multiline={true}
                            />
                        </Grid>
                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <StringInputField
                                name="erpSoftwareVersion"
                                value={model?.erpSoftwareVersion}
                                label={t(CompanyI18N.form.field.erpSoftwareVersion)}
                                error={errors?.erpSoftwareVersion}
                                maxChars={Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX}
                                onChange={onChange}
                                multiline={true}
                            />
                        </Grid>

                        <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                            <StringInputField
                                name="additionalInformation"
                                value={model?.additionalInformation}
                                label={t(CompanyI18N.form.field.additionalInformation)}
                                error={errors?.additionalInformation}
                                maxChars={Constants.ADDITIONAL_INFORMATION_LENGTH_MAX}
                                onChange={onChange}
                                multiline={true}
                            />
                        </Grid>
                    </Grid>
                </CardEx>
            </Grid>
        </Grid>
    );
};
